import { Hovercard, Icon } from "@resource/atlas";
import { atlasChevronLeft, atlasChevronRight } from "@resource/atlas/icons";
import { Button } from "ariakit";
import clsx from "clsx";
import React, {
  forwardRef,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from "react";

import MobileStageStepper from "./MobileStageStepper";
import StepIndicator from "./StepIndicator";
import useHorizontalScrollOverflow from "./use-horizontal-scroll-overflow";

// config
// ------

const SCROLL_INCREMENT_WIDTH = 112 + 18; // stage width + gap (in pixels)
const SCROLL_INCREMENT_STAGES = 3; // stages per scroll
const SCROLL_EDGE_SNAPPING_THRESHOLD = 50;

// shared types
// ------------

type StageState = false | "COMPLETED" | "CURRENT" | "LOCKED";
type StageContext = {
  previous: StageState;
  current: StageState;
  next: StageState;
};

// stage line

type StageLineProps = {
  isLocked?: boolean;
  targetState: StageState;
  direction: "left" | "right";
};

function StageLine({ isLocked, targetState, direction }: StageLineProps) {
  const lineColor =
    isLocked || targetState === "LOCKED"
      ? "bg-org-primary-light"
      : "bg-org-primary";

  return (
    <>
      {targetState && (
        <div
          className={clsx(
            "absolute h-1 w-1/2 top-2",
            {
              "left-0": direction === "left",
              "right-0": direction === "right",
            },
            lineColor
          )}
        />
      )}
    </>
  );
}

// step component

export interface StageStepStage {
  id: string;
  title: string;
  position: number;
  description: string | null;
  needsFeedback: boolean;
}

type StageStepProps = {
  stage: StageStepStage;
  isActive: boolean;
  allowNavigationToLockedStages: boolean;
  interactive: boolean;
  context: StageContext;
  onSelect: () => void;
};

const StageStep = forwardRef<HTMLButtonElement, StageStepProps>(
  (
    {
      stage,
      allowNavigationToLockedStages,
      isActive,
      context,
      onSelect,
      interactive,
    },
    ref
  ) => {
    const isCurrent = context.current === "CURRENT";
    const isLocked = context.current === "LOCKED";
    const isInteractive =
      interactive && !isActive && (allowNavigationToLockedStages || !isLocked);
    const hovercardContent = stage.needsFeedback
      ? "Please rate your interview."
      : stage.description ?? undefined;

    return (
      <Hovercard.Root
        placement="top"
        open={hovercardContent ? undefined : false}
        timeout={0}
      >
        <Hovercard.Anchor>
          <Button
            accessibleWhenDisabled={Boolean(hovercardContent)}
            aria-label={`Go to "${stage.title}" stage`}
            ref={ref}
            type="button"
            className={clsx(
              "self-start w-[7rem] flex-shrink-0 rounded basis-[7rem]",
              "focus:outline-none focus-visible:focus-ring focus-visible:z-10",
              isInteractive ? "cursor-pointer" : "cursor-default"
            )}
            onClick={onSelect}
            disabled={!isInteractive}
          >
            <div className="relative mb-2 flex justify-center">
              <StageLine
                isLocked={isLocked}
                targetState={context.previous}
                direction="left"
              />
              <StageLine
                isLocked={isLocked}
                targetState={context.next}
                direction="right"
              />
              <StepIndicator isCurrent={isCurrent} isLocked={isLocked} />
            </div>
            <div className="flex justify-center">
              <p
                className={clsx(
                  "text-center break-words select-none",
                  !isActive && isLocked ? "text-disabled" : "text-dark",
                  isActive ? "text-body-md-heavy" : "text-body-md"
                )}
              >
                {stage.title}
                {stage.needsFeedback ? (
                  <b className="text-warning-700 whitespace-nowrap"> *</b>
                ) : undefined}
              </p>
            </div>
          </Button>
        </Hovercard.Anchor>
        <Hovercard.Content hasPadding={false}>
          <p className="px-4 py-3 text-dark text-body-md">{hovercardContent}</p>
        </Hovercard.Content>
      </Hovercard.Root>
    );
  }
);

// stage stepper
// -------------

// stepper arrows

type StepperArrowProps = {
  direction: "left" | "right";
  isVisible?: boolean;
  onClick: () => void;
};

function StepperArrow({ direction, isVisible, onClick }: StepperArrowProps) {
  return (
    <button
      type="button"
      aria-label={`Scroll stage stepper ${direction}`}
      className={clsx(
        "absolute top-1/2 -translate-y-1/2 w-8 h-8 z-20",
        "focus:outline-none focus-visible:focus-ring",
        {
          "-left-4": direction === "left",
          "-right-4": direction === "right",
        },
        "bg-org-primary flex items-center justify-center rounded-full text-white cursor-pointer transition-opacity",
        !isVisible && "opacity-0 pointer-events-none"
      )}
      disabled={!isVisible}
      onClick={onClick}
    >
      <Icon
        content={direction === "left" ? atlasChevronLeft : atlasChevronRight}
      />
    </button>
  );
}

function useStepperArrows() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isOverflowingLeft, isOverflowingRight } =
    useHorizontalScrollOverflow(scrollRef);

  const scroll = useCallback((direction: "left" | "right") => {
    const el = scrollRef.current;
    const isLeft = direction === "left";

    if (!el) return;

    const scrollDistance = SCROLL_INCREMENT_STAGES * SCROLL_INCREMENT_WIDTH;

    const targetScrollLeft = isLeft
      ? el.scrollLeft - scrollDistance
      : el.scrollLeft + scrollDistance;

    const targetDistanceToEdge = isLeft
      ? targetScrollLeft
      : el.scrollWidth - el.clientWidth - targetScrollLeft;
    const isInEdgeSnappingThreshold =
      targetDistanceToEdge < SCROLL_EDGE_SNAPPING_THRESHOLD;

    const edgeScrollLeft = isLeft ? 0 : el.clientWidth;

    el.scrollLeft = isInEdgeSnappingThreshold
      ? edgeScrollLeft
      : targetScrollLeft;
  }, []);

  const leftArrow = (
    <StepperArrow
      direction="left"
      isVisible={isOverflowingLeft}
      onClick={() => scroll("left")}
    />
  );

  const rightArrow = (
    <StepperArrow
      direction="right"
      isVisible={isOverflowingRight}
      onClick={() => scroll("right")}
    />
  );

  return { scrollRef, leftArrow, rightArrow };
}

// step separator

type StepSeparatorProps = {
  isLocked?: boolean;
  targetState: StageState;
};

function StepSeparator({ isLocked, targetState }: StepSeparatorProps) {
  if (!targetState) return null;

  const lineColor =
    isLocked || targetState === "LOCKED"
      ? "bg-org-primary-light"
      : "bg-org-primary";

  return (
    <>
      <div className="relative min-w-[1.125rem] flex-grow flex-shrink-0">
        <div
          className={clsx("absolute h-1 w-[calc(100%+.5rem)] top-2", lineColor)}
        />
      </div>
    </>
  );
}

// steps

type UseStepsProps = {
  stages: StageStepStage[];
  activeIndex: number;
  currentIndex: number;
  allowNavigationToLockedStages: boolean;
  interactive: boolean;
  onSelectStageId: (stageId: string) => void;
};

function useSteps({
  stages,
  activeIndex,
  currentIndex,
  allowNavigationToLockedStages,
  interactive,
  onSelectStageId,
}: UseStepsProps) {
  function computeStageState(index: number): StageState {
    if (index < 0 || index >= stages.length) return false;
    if (index === currentIndex) return "CURRENT";
    if (index <= currentIndex) return "COMPLETED";
    if (index > currentIndex) return "LOCKED";
    throw new Error("Unexpected error");
  }

  const contexts: StageContext[] = stages.map((_, i) => ({
    previous: computeStageState(i - 1),
    current: computeStageState(i),
    next: computeStageState(i + 1),
  }));

  return stages.map((stage, index) => {
    const ctx = contexts[index];

    return (
      <React.Fragment key={stage.id}>
        <StageStep
          stage={stage}
          isActive={index === activeIndex}
          context={ctx}
          interactive={interactive}
          allowNavigationToLockedStages={allowNavigationToLockedStages}
          onSelect={() => onSelectStageId(stage.id)}
        />
        <StepSeparator
          isLocked={ctx.current === "LOCKED"}
          targetState={ctx.next}
        />
      </React.Fragment>
    );
  });
}

// initial stepper scroll

type UseInitialStepperScrollProps = {
  scrollRef: RefObject<HTMLElement>;
  activeIndex: number;
};

function useScrollToActiveStage({
  scrollRef,
  activeIndex,
}: UseInitialStepperScrollProps) {
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;
    // scroll to the previous stage, we want it to be visible too
    const targetIndex = activeIndex >= 0 ? activeIndex - 1 : 0;
    scrollElement.scrollLeft = targetIndex * SCROLL_INCREMENT_WIDTH;
  }, [activeIndex, scrollRef]);
}

// stepper component

// also known as Safari
const IS_INTERNET_EXPLORER =
  typeof window !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);

// stage step
// ----------

type StageStepperProps = {
  stages: StageStepStage[];
  selectedStageId: string;
  currentStageId?: string;
  onSelectStageId(stageId: string): void;
  interactive?: boolean;
  allowNavigationToLockedStages?: boolean;
};

function StageStepper({
  interactive = true,
  allowNavigationToLockedStages = false,
  ...props
}: StageStepperProps) {
  const {
    currentStageId,
    onSelectStageId,
    selectedStageId: activeStageId,
    stages,
  } = props;
  const activeIndex = stages.findIndex((stage) => stage.id === activeStageId);
  const currentIndex = stages.findIndex((stage) => stage.id === currentStageId);

  const steps = useSteps({
    stages,
    activeIndex,
    currentIndex,
    allowNavigationToLockedStages,
    onSelectStageId,
    interactive,
  });

  const { scrollRef, leftArrow, rightArrow } = useStepperArrows();
  useScrollToActiveStage({ scrollRef, activeIndex });

  // No need to show the tracker if there's less than 2 stages
  if (stages.length < 2) {
    return null;
  }

  return (
    <>
      <div className="hidden min-[37.5rem]:block relative my-6 mx-[-1.4rem]">
        {interactive && leftArrow}
        <div
          ref={scrollRef}
          className={clsx(
            "flex px-[1.4rem] py-2 scroll-smooth",
            IS_INTERNET_EXPLORER ? "overflow-auto" : "overflow-hidden"
          )}
        >
          {steps}
        </div>
        {interactive && rightArrow}
      </div>
      <div className="min-[37.5rem]:hidden">
        <MobileStageStepper
          {...props}
          interactive={interactive}
          allowNavigationToLockedStages={allowNavigationToLockedStages}
        />
      </div>
    </>
  );
}

export default StageStepper;
